import React from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { useMediaQuery, useTheme } from "@material-ui/core";
import { useIntl, IntlShape } from "react-intl";
import {
  useSnackbar,
  SnackbarMessage,
  OptionsObject,
  SnackbarKey,
} from "notistack";
import {
  MeetingStatusType,
  MeetingApi,
  UpdateMeetingStatusOperationRequest,
} from "../../../generated/meeting-api";
import { useCustomerId } from "../../../Providers/CustomerProvider/CustomerProvider";
import { messages } from "./CancelMeeting.messages";
import { meetingService } from "../../../Providers/ServiceProvider/ServiceProvider";

interface OnVerifyProps {
  meetingId: string;
  customerId: string;
  meetingServiceApi: MeetingApi;
  intl: IntlShape;
  onCanceledMeeting?: () => void;
  enqueueSnackbar: (
    message: SnackbarMessage,
    options?: OptionsObject
  ) => SnackbarKey;
}
const onVerify = (props: OnVerifyProps) => {
  const {
    meetingId,
    customerId,
    meetingServiceApi,
    enqueueSnackbar,
    onCanceledMeeting,
    intl,
  } = props;
  const meetingRequest: UpdateMeetingStatusOperationRequest = {
    customerId,
    meetingId,
    updateMeetingStatusRequest: { newStatus: MeetingStatusType.Cancelled },
  };
  meetingServiceApi
    .updateMeetingStatus(meetingRequest)
    .then(() =>
      enqueueSnackbar(intl.formatMessage(messages.success), {
        variant: "success",
      })
    )
    .then(onCanceledMeeting)
    .catch(() =>
      enqueueSnackbar(intl.formatMessage(messages.generalError), {
        variant: "error",
      })
    );
};

interface Props {
  isOpen: boolean;
  onClose: (event: React.MouseEvent) => void;
  meetingId: string;
  meetingTitle: string;
  onCanceledMeeting?: () => void;
}

const CancelMeetingDialog = (props: Props) => {
  const { isOpen, meetingId, meetingTitle, onClose, onCanceledMeeting } = props;
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const customerId = useCustomerId() || "";
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const handleSubmit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onVerify({
      meetingId,
      customerId,
      meetingServiceApi: meetingService(),
      intl,
      enqueueSnackbar,
      onCanceledMeeting,
    });
    onClose(e);
  };

  return (
    <Dialog
      onClick={(e) => e.stopPropagation()}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onBackdropClick={onClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        {intl.formatMessage(messages.title, { meetingTitle })}
      </DialogTitle>
      <DialogContent>{intl.formatMessage(messages.content)}</DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleSubmit} color="primary">
          {intl.formatMessage(messages.submitButtonLabel)}
        </Button>
        <Button onClick={onClose} color="primary">
          {intl.formatMessage(messages.cancelButtonLabel)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CancelMeetingDialog;
